import {
  FiHome,
  FiActivity,
  FiGlobe,
  FiFile,
  FiMonitor,
  FiBarChart2,
  FiClock,
  FiBriefcase,
  FiFileText,
  FiCalendar,
  FiAward,
  FiBell,
  FiMessageSquare,
  FiSettings,
  FiTarget,
  FiCpu,
  FiUsers,
  FiServer,
  FiDownload,
  FiUserCheck,
  FiMail,
  FiSearch
} from 'react-icons/fi'

const ROLE_USER = 'user'
const ROLE_ADMIN = 'admin'
const ROLE_SUPERADMIN = 'superadmin'

function normalizeRole(role) {
  if (role === ROLE_ADMIN || role === ROLE_SUPERADMIN) return role
  return ROLE_USER
}

function getPlanType(user) {
  const pt = user?.tenant?.planType
  const n = parseInt(pt, 10)
  return Number.isFinite(n) ? n : 1
}

function hasAnyRole(userRole, allowedRoles) {
  if (!allowedRoles || allowedRoles.length === 0) return true
  return allowedRoles.includes(userRole)
}

function isItemVisible(item, user) {
  const role = normalizeRole(user?.role)
  const planType = getPlanType(user)

  if (item.minPlanType && planType < item.minPlanType) return false
  if (!hasAnyRole(role, item.roles)) return false
  return true
}

function filterItems(items, user) {
  return (items || [])
    .map((item) => {
      const next = { ...item }
      if (next.children) {
        next.children = filterItems(next.children, user)
      }
      return next
    })
    .filter((item) => {
      if (!isItemVisible(item, user)) return false
      if (item.children && item.children.length === 0) return false
      return true
    })
}

// Mission-based groups (control room)
export const NAV_GROUPS = [
  {
    id: 'observe',
    label: 'Observar',
    items: [
      { id: 'dashboard', path: '/', icon: FiHome, label: 'Dashboard', exact: true },
      { id: 'activities', path: '/activities', icon: FiActivity, label: 'Atividades' }
    ]
  },
  {
    id: 'investigate',
    label: 'Investigar',
    items: [
      { id: 'internet', path: '/internet', icon: FiGlobe, label: 'Internet', minPlanType: 2 },
      { id: 'file-ops', path: '/file-operations', icon: FiFile, label: 'Operações de Arquivos', minPlanType: 2 },
      { id: 'app-analytics', path: '/app-analytics', icon: FiMonitor, label: 'Aplicativos', minPlanType: 2 },
      {
        id: 'analytics',
        path: '/analytics',
        icon: FiBarChart2,
        label: 'Análises',
        minPlanType: 2,
        children: [
          { id: 'analytics-overview', path: '/analytics', label: 'Visão Geral', exact: true, minPlanType: 2 },
          { id: 'analytics-work-hours', path: '/analytics/work-hours', label: 'Horários de Trabalho', icon: FiClock, minPlanType: 2 },
          { id: 'analytics-departments', path: '/analytics/departments', label: 'Por Departamento', icon: FiBriefcase, minPlanType: 2 }
        ]
      },
      { id: 'user-report', path: '/user-report', icon: FiFileText, label: 'Relatório Individual', minPlanType: 2 },
      {
        id: 'reports',
        path: '/reports',
        icon: FiCalendar,
        label: 'Relatórios',
        minPlanType: 2,
        children: [
          { id: 'reports-daily', path: '/reports/daily', label: 'Diário', minPlanType: 2 },
          { id: 'reports-weekly', path: '/reports/weekly', label: 'Semanal', minPlanType: 2 },
          { id: 'reports-monthly', path: '/reports/monthly', label: 'Mensal', minPlanType: 2 }
        ]
      },
      { id: 'goals-dashboard', path: '/goals/dashboard', icon: FiAward, label: 'Dashboard de Metas' }
    ]
  },
  {
    id: 'act',
    label: 'Agir',
    items: [
      { id: 'alerts', path: '/alerts', icon: FiBell, label: 'Alertas', minPlanType: 2 },
      { id: 'chat', path: '/chat', icon: FiMessageSquare, label: 'Chat IA', minPlanType: 3 },
      {
        id: 'keywords',
        path: '/keyword-search',
        icon: FiSearch,
        label: 'Palavras-chave',
        minPlanType: 2,
        children: [
          { id: 'keyword-search', path: '/keyword-search', label: 'Buscar', minPlanType: 2 },
          { id: 'keyword-monitoring', path: '/keyword-monitoring', label: 'Monitoramento', minPlanType: 2 },
          { id: 'keyword-occurrences', path: '/keyword-occurrences', label: 'Ocorrências', minPlanType: 2 }
        ]
      }
    ]
  },
  {
    id: 'admin',
    label: 'Administrar',
    items: [
      {
        id: 'settings',
        path: '/settings',
        icon: FiSettings,
        label: 'Configurações',
        children: [
          { id: 'settings-general', path: '/settings', label: 'Geral', exact: true },
          { id: 'settings-goals', path: '/settings/goals', label: 'Metas', icon: FiTarget },
          { id: 'settings-ai', path: '/settings/ai', label: 'IA', icon: FiCpu }
        ]
      },
      // Admin / Superadmin
      { id: 'admin-colaboradores', path: '/admin/colaboradores', icon: FiUserCheck, label: 'Colaboradores', roles: [ROLE_ADMIN, ROLE_SUPERADMIN] },
      { id: 'admin-import', path: '/admin/import', icon: FiDownload, label: 'Importar Dados', roles: [ROLE_ADMIN, ROLE_SUPERADMIN] },
      { id: 'admin-site-categories', path: '/admin/site-categories', icon: FiGlobe, label: 'Categorias de Sites', roles: [ROLE_ADMIN, ROLE_SUPERADMIN] },
      { id: 'admin-app-categories', path: '/admin/app-categories', icon: FiMonitor, label: 'Categorias de Apps', roles: [ROLE_ADMIN, ROLE_SUPERADMIN] },
      { id: 'admin-ai-metrics', path: '/admin/ai-metrics', icon: FiBarChart2, label: 'Análise de IA', roles: [ROLE_ADMIN, ROLE_SUPERADMIN] },
      { id: 'admin-smtp', path: '/admin/smtp-settings', icon: FiMail, label: 'Configuração de Email', roles: [ROLE_ADMIN, ROLE_SUPERADMIN] },
      { id: 'admin-chat-history', path: '/admin/chat-history', icon: FiMessageSquare, label: 'Histórico do Chat', roles: [ROLE_ADMIN, ROLE_SUPERADMIN], minPlanType: 3 },
      // Superadmin
      { id: 'superadmin-users', path: '/admin/users', icon: FiUsers, label: 'Usuários', roles: [ROLE_SUPERADMIN] },
      { id: 'superadmin-tenants', path: '/admin/tenants', icon: FiServer, label: 'Empresas', roles: [ROLE_SUPERADMIN] },
      { id: 'superadmin-ai-analysis', path: '/admin/ai-analysis', icon: FiFileText, label: 'Tabelas de Análise IA', roles: [ROLE_SUPERADMIN] },
      { id: 'superadmin-chat-all', path: '/admin/chat-history-all', icon: FiServer, label: 'Chat - Todos Tenants', roles: [ROLE_SUPERADMIN], minPlanType: 3 }
    ]
  }
]

export function getNavGroups(user) {
  return NAV_GROUPS
    .map((group) => ({
      ...group,
      items: filterItems(group.items, user)
    }))
    .filter((group) => group.items.length > 0)
}
